import { getParent, types, type Instance } from 'mobx-state-tree';
import AttributesInspector from './attributes_inspector';
import type { IInspectorParent } from './inspector_parent';

/** The change keys stored by the method inspector. */
type MethodChangeKey = 'name' | 'type' | 'precondition' | 'postcondition' | 'implementation';

/**
 * Store the changes in the method inspector that are currently pending.
 */
const MethodInspectorChanges = types
  .model('MethodInspectorChanges', {
    name: types.optional(types.maybeNull(types.string), null),
    type: types.optional(types.maybeNull(types.string), null),
    precondition: types.optional(types.maybeNull(types.string), null),
    postcondition: types.optional(types.maybeNull(types.string), null),
    implementation: types.optional(types.maybeNull(types.string), null),
  })
  .views((self) => ({
    // the inspector is dirty if any of the attributes has changes
    get dirty() {
      return (
        self.name !== null ||
        self.type !== null ||
        self.precondition !== null ||
        self.postcondition !== null ||
        self.implementation !== null
      );
    },
  }))
  .actions((self) => ({
    /**
     * Store a change for the given key.
     *
     * This action also receives the original value of the attribute and clears
     * the store change if the new value for the change is the same as the
     * original value.
     */
    storeChange(key: MethodChangeKey, original: string | null, change: string | null) {
      if (original !== change) {
        self[key] = change;
      } else {
        self[key] = null;
      }
    },
    discard() {
      self.name = null;
      self.type = null;
      self.precondition = null;
      self.postcondition = null;
      self.implementation = null;
    },
  }));

/**
 * UI state for the method inspector
 */
const MethodInspector = types
  .model('MethodInspector', {
    // the pending changes
    changes: types.optional(MethodInspectorChanges, () => MethodInspectorChanges.create({})),
    // the state for the nested attributes inspector, see
    // `attributes_inspector.js`
    attributesInspector: types.optional(AttributesInspector, () => AttributesInspector.create({})),
  })
  .views((self) => ({
    // We are dirty when we have changes ourself or the nested attributes
    // inspector is dirty
    get dirty() {
      return self.changes.dirty || self.attributesInspector.dirty;
    },
  }))
  .actions((self) => ({
    // Discard our own changes and those of the nested attributes inspector
    discardChanges() {
      self.changes.discard();
      self.attributesInspector.discardChanges();
    },
    setProjectBusy(busy: boolean) {
      getParent<IInspectorParent>(self).setProjectBusy(busy);
    },
  }));

export type IMethodInspector = Instance<typeof MethodInspector>;

export default MethodInspector;
